import React, { useMemo, useState } from "react";
import {
  Box,
  Dialog,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  ListItemButton,
  OutlinedInput,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import {
  getCountries,
  getCountryCallingCode,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import { getExampleNumber } from "libphonenumber-js/min";
import examples from "libphonenumber-js/mobile/examples";
import en from "react-phone-number-input/locale/en.json";

const getFlagEmoji = (countryCode) =>
  countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));

const buildCountryOptions = (preferredCountries = []) => {
  const preferredSet = new Set(preferredCountries.map((c) => c.toUpperCase()));
  const options = getCountries().map((code) => ({
    code,
    name: en[code] || code,
    dialCode: getCountryCallingCode(code),
  }));

  options.sort((a, b) => {
    const aPreferred = preferredSet.has(a.code);
    const bPreferred = preferredSet.has(b.code);
    if (aPreferred && !bPreferred) return -1;
    if (!aPreferred && bPreferred) return 1;
    return a.name.localeCompare(b.name);
  });

  return options;
};

const filterCountryList = (options, query) => {
  const normalized = query.trim().toLowerCase().replace(/^\+/, "");
  if (!normalized) return options;

  return options.filter(
    ({ code, name, dialCode }) =>
      name.toLowerCase().includes(normalized) ||
      code.toLowerCase().includes(normalized) ||
      dialCode.includes(normalized)
  );
};

const getMobileDigitLimit = (countryCode) => {
  try {
    const example = getExampleNumber(countryCode, examples);
    return example?.nationalNumber?.length || 15;
  } catch {
    return 15;
  }
};

const limitNationalDigits = (digits, countryCode) => {
  if (!digits) return "";
  return digits.slice(0, getMobileDigitLimit(countryCode));
};

const buildFullNumber = (country, digits) => `+${country.dialCode}${digits}`;

const validatePhoneForCountry = (fullNumber, countryCode) =>
  Boolean(fullNumber) && isValidPhoneNumber(fullNumber, countryCode);

const getPhoneValidationMessage = (countryCode) => {
  const digitLimit = getMobileDigitLimit(countryCode);
  return digitLimit < 15
    ? `Enter a valid ${digitLimit}-digit phone number`
    : "Enter a valid phone number";
};

const getPhoneValidationState = (country, digits) => {
  if (!digits) {
    return { isValid: false, showError: false, message: "" };
  }

  const fullNumber = buildFullNumber(country, digits);
  const isValid = validatePhoneForCountry(fullNumber, country.code);
  const digitLimit = getMobileDigitLimit(country.code);
  const showError =
    !isValid &&
    (digits.length >= digitLimit || !isPossiblePhoneNumber(fullNumber, country.code));

  return {
    isValid,
    showError,
    message: showError ? getPhoneValidationMessage(country.code) : "",
  };
};

const SearchablePhoneInput = ({
  value = "",
  onChange,
  defaultCountry = "IN",
  preferredCountries = ["IN", "GB", "SG"],
  label = "",
  placeholder = "Phone number",
  error = false,
  helperText = "",
  fullWidth = true,
  height = "6vh",
  sx = {},
}) => {
  const countryOptions = useMemo(
    () => buildCountryOptions(preferredCountries),
    [preferredCountries]
  );

  const getDefaultCountry = () =>
    countryOptions.find((c) => c.code === defaultCountry.toUpperCase()) ||
    countryOptions[0];

  const [selectedCountry, setSelectedCountry] = useState(getDefaultCountry);
  const [nationalNumber, setNationalNumber] = useState("");
  const [countryOpen, setCountryOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [validationMessage, setValidationMessage] = useState("");

  const filteredCountries = useMemo(
    () => filterCountryList(countryOptions, searchValue),
    [countryOptions, searchValue]
  );

  React.useEffect(() => {
    if (!value) return;
    try {
      const parsed = parsePhoneNumber(value);
      if (parsed?.country) {
        const country =
          countryOptions.find((c) => c.code === parsed.country) ||
          getDefaultCountry();
        setSelectedCountry(country);
        setNationalNumber(parsed.nationalNumber || "");
      }
    } catch {
      // keep current local state if value cannot be parsed
    }
  }, [value, countryOptions]);

  const emitChange = (country, digits) => {
    const { isValid, showError, message } = getPhoneValidationState(
      country,
      digits
    );
    setValidationMessage(showError ? message : "");

    if (!digits) {
      onChange?.("", { countryCode: country.code, isValid: false });
      return;
    }

    onChange?.(buildFullNumber(country, digits), {
      countryCode: country.code,
      isValid,
    });
  };

  const handleOpenCountryPicker = () => {
    setSearchValue("");
    setCountryOpen(true);
  };

  const handleCloseCountryPicker = () => {
    setCountryOpen(false);
    setSearchValue("");
  };

  const handleSelectCountry = (country) => {
    setSelectedCountry(country);
    emitChange(country, nationalNumber.replace(/\D/g, ""));
    handleCloseCountryPicker();
  };

  const handlePhoneChange = (event) => {
    const digits = limitNationalDigits(
      event.target.value.replace(/\D/g, ""),
      selectedCountry.code
    );
    setNationalNumber(digits);
    emitChange(selectedCountry, digits);
  };

  return (
    <Box sx={{ width: fullWidth ? "100%" : "auto", ...sx }}>
      {label && (
        <Typography
          variant="caption"
          sx={{
            color: error ? "#d32f2f" : "rgba(0, 0, 0, 0.6)",
            ml: 1.5,
            mb: 0.5,
            display: "block",
          }}
        >
          {label}
        </Typography>
      )}
      <FormControl fullWidth error={error || Boolean(validationMessage)}>
        <OutlinedInput
          value={nationalNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          type="tel"
          inputProps={{ inputMode: "numeric" }}
          startAdornment={
            <InputAdornment
              position="start"
              sx={{
                mr: 1,
                height: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                onClick={handleOpenCountryPicker}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  pl: 0.5,
                  pr: 1,
                  borderRight: "1px solid rgba(0, 0, 0, 0.23)",
                  userSelect: "none",
                }}
              >
                <span>{getFlagEmoji(selectedCountry.code)}</span>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: "uppercase",
                    color: "#000000",
                  }}
                >
                  {selectedCountry.code}
                </Typography>
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 18, color: "#000000" }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: "text.primary",
                  mr: 0.5,
                }}
              >
                +{selectedCountry.dialCode}
              </Typography>
            </InputAdornment>
          }
          sx={{
            height,
            borderRadius: "5px",
            backgroundColor: "#FFFFFF",
            fontSize: 16,
            pl: 1,
            "& .MuiOutlinedInput-input": {
              pl: 1,
            },
          }}
        />
        {helperText || validationMessage ? (
          <FormHelperText
            error={error || Boolean(validationMessage)}
            sx={{ mx: 0, mt: 0.25, fontSize: 12, lineHeight: 1.2 }}
          >
            {helperText || validationMessage}
          </FormHelperText>
        ) : null}
      </FormControl>

      <Dialog
        fullScreen
        open={countryOpen}
        onClose={handleCloseCountryPicker}
        PaperProps={{
          sx: {
            maxWidth: 600,
            mx: "auto",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1,
            py: 1.5,
            borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
          }}
        >
          <IconButton onClick={handleCloseCountryPicker} aria-label="Close">
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ flex: 1, textAlign: "center", fontSize: 18, fontWeight: 600 }}
          >
            Select country
          </Typography>
          <Box sx={{ width: 40 }} />
        </Box>

        <Box sx={{ px: 2, py: 2 }}>
          <TextField
            fullWidth
            autoFocus
            placeholder="Search country or code"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 2,
            pb: 3,
          }}
        >
          {filteredCountries.length === 0 ? (
            <Typography
              sx={{ textAlign: "center", color: "text.secondary", py: 4 }}
            >
              No countries found
            </Typography>
          ) : (
            filteredCountries.map((country) => {
              const isSelected = country.code === selectedCountry.code;
              return (
                <ListItemButton
                  key={country.code}
                  selected={isSelected}
                  onClick={() => handleSelectCountry(country)}
                  sx={{
                    py: 1.25,
                    px: 2,
                    mb: 1.25,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: isSelected
                      ? "primary.main"
                      : "rgba(0, 0, 0, 0.08)",
                    bgcolor: isSelected ? "rgba(25, 118, 210, 0.04)" : "#fff",
                    flexGrow: 0,
                    height: "auto",
                    width: "100%",
                  }}
                >
                  <Box sx={{ fontSize: 20, mr: 1.5, lineHeight: 1, flexShrink: 0 }}>
                    {getFlagEmoji(country.code)}
                  </Box>
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: 15,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {country.name}{" "}
                    <Typography
                      component="span"
                      sx={{ color: "text.secondary", fontWeight: 400 }}
                    >
                      +{country.dialCode}
                    </Typography>
                  </Typography>
                  {isSelected && (
                    <CheckIcon color="primary" sx={{ ml: 1, flexShrink: 0 }} />
                  )}
                </ListItemButton>
              );
            })
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export {
  isValidPhoneNumber,
  validatePhoneForCountry,
  getPhoneValidationMessage,
};
export default SearchablePhoneInput;
